import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject, map, takeUntil } from 'rxjs';
import { AxiosError } from 'axios';
import { CasesService } from 'src/app/services/cases/cases.service';
import {
  COFACE_FORM_NAME,
  SUMMARY_FORM_NAME,
} from 'src/app/services/form/form.factory';
import { SummaryForm } from 'src/app/services/form/form.controls';
import {
  PaymentStepForm,
  ProductType,
  Summary,
} from 'src/app/services/form/form.resources';
import { FormService } from 'src/app/services/form/form.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { caseNotDraftError } from '../../case-form.component';
import {
  StartWithTransferDialogComponent,
  StartWithTransferDialogData,
} from './start-with-transfer-dialog/start-with-transfer-dialog.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { PayeeToggleComponent } from '../../../../shared/payee-toggle/payee-toggle.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { CofaceFormComponent } from './coface-form/coface-form.component';
import { NoticeFormComponent } from './notice-form/notice-form.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { DecimalPipe, DatePipe, KeyValuePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-summary-form',
  templateUrl: './summary-form.component.html',
  styleUrls: ['./summary-form.component.scss'],
  imports: [
    PayeeIconComponent,
    PayeeToggleComponent,
    MatTooltip,
    MatRadioGroup,
    FormsModule,
    ReactiveFormsModule,
    MatRadioButton,
    CofaceFormComponent,
    NoticeFormComponent,
    MatProgressSpinner,
    MatIcon,
    DecimalPipe,
    DatePipe,
    KeyValuePipe,
    TranslateModule,
  ],
})
export class SummaryFormComponent implements OnInit, OnDestroy {
  private formService = inject(FormService);
  private helpersService = inject(HelpersService);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private casesService = inject(CasesService);
  readonly loading = toSignal(
    this.formService.saving.pipe(map((value) => value > 0)),
    { initialValue: false },
  );
  private readonly optionalClaimForms: PaymentStepForm[] = [
    'flat_rate_cost_added_form',
    'interest_added_form',
    'start_case_price_added_form',
  ];
  private readonly destroy = new Subject<void>();

  readonly paymentMethod = this.fb.control('card');
  hasUnsavedChanges = false;

  get form(): SummaryForm {
    return this.formService.form.controls[SUMMARY_FORM_NAME]!;
  }
  get summary(): Summary {
    return this.formService.forms.find(({ name }) => name === 'summary')
      ?.data as Summary;
  }

  get productType() {
    return this.form.controls.product_type;
  }
  get isStartCasePriceClaimAdded() {
    return this.form.controls.payee_case.controls
      .is_start_case_price_claim_added;
  }
  get isInterestAdded() {
    return this.form.controls.payee_case.controls.is_interest_added;
  }
  get isFlatRateCostClaimAdded() {
    return this.form.controls.payee_case.controls.is_flat_rate_cost_claim_added;
  }

  get showProductForms(): boolean {
    return this.summary.product.is_product_type_editable;
  }
  get productForms(): PaymentStepForm[] {
    return this.summary.product.forms[this.summary.product.type];
  }
  get showOptionalClaimsForm(): boolean {
    return (
      this.isSelectedProductAvailable &&
      this.productForms.some((form) => this.optionalClaimForms.includes(form))
    );
  }
  get showDebtorForm(): boolean {
    return (
      this.isSelectedProductAvailable &&
      this.productForms.includes('debtor_form')
    );
  }
  get showFlatRateCostAddedForm(): boolean {
    return (
      this.isSelectedProductAvailable &&
      this.summary.payee_case.has_claims_eligible_for_flat_rate_cost &&
      this.productForms.includes('flat_rate_cost_added_form')
    );
  }
  get showInterestAddedForm(): boolean {
    return (
      this.isSelectedProductAvailable &&
      this.productForms.includes('interest_added_form')
    );
  }
  get showStartCasePriceAddedForm(): boolean {
    return (
      this.isSelectedProductAvailable &&
      this.productForms.includes('start_case_price_added_form')
    );
  }
  get showCofaceForm(): boolean {
    return (
      this.isSelectedProductAvailable &&
      this.productForms.includes(COFACE_FORM_NAME)
    );
  }

  get isSelectedProductAvailable(): boolean {
    return this.isProductAvailable(this.productType.value);
  }

  get selectedProductUnavailableReasons(): string[] {
    if (this.isSelectedProductAvailable) {
      return [];
    }

    return this.getProductUnavailableReasons(this.productType.value);
  }

  isProductAvailable(type: ProductType): boolean {
    if (this.getProductUnavailableReasons(type).length > 0) {
      return false;
    }

    return this.isActiveProduct(type);
  }

  shouldShowProduct(type: ProductType): boolean {
    return this.isActiveProduct(type);
  }

  private isActiveProduct(type: ProductType): boolean {
    const activeProducts = this.summary.active_products;
    return Array.isArray(activeProducts) && activeProducts.includes(type);
  }

  private getProductUnavailableReasons(type: ProductType): string[] {
    const reasons = this.summary.product_unavailable?.reasons?.[type];
    return Array.isArray(reasons) ? reasons : [];
  }

  get showAllClaimsAmount(): boolean {
    return (
      (!this.isFlatRateCostClaimAdded.value ||
        this.summary.payee_case.flat_rate_cost_claims_sum !== null) &&
      (!this.isInterestAdded.value ||
        this.summary.payee_case.interest_amount_claims_sum !== null)
    );
  }

  get flatRateCostAmount(): string {
    return (
      this.summary.payee_case.flat_rate_cost_claims_sum?.toLocaleString(
        'hu-HU',
      ) ?? ''
    );
  }

  get interestAmount(): string | null {
    if (!this.summary.payee_case.interest_amount_claims_sum) {
      return null;
    }

    // Format all currencies in the interest amount sums
    const formattedAmounts = Object.entries(
      this.summary.payee_case.interest_amount_claims_sum,
    )
      .map(
        ([currency, sum]) =>
          `${sum.remaining_amount.toLocaleString('hu-HU')} ${currency}`,
      )
      .join(' | ');

    return formattedAmounts;
  }

  get startCaseFeeAmount(): number | null {
    if (this.summary.fees.start.some((fee) => fee.amount === null)) {
      return null;
    }

    return this.summary.fees.start.reduce((sum, fee) => sum + fee.amount!, 0);
  }
  get successCaseFeeAmount(): number {
    if (
      this.summary.fees['closed-success'].some((fee) => fee.amount === null)
    ) {
      return 0;
    }

    return this.summary.fees['closed-success'].reduce(
      (sum, fee) => sum + fee.amount!,
      0,
    );
  }

  get startFeesCurrencyIso(): string {
    return this.summary.fees.start[0]?.currency_iso ?? 'HUF';
  }

  get successFeesCurrencyIso(): string {
    return this.summary.fees['closed-success'][0]?.currency_iso ?? 'HUF';
  }

  async ngOnInit() {
    this.bindFormSubscriptions();

    this.formService.saving.pipe(takeUntil(this.destroy)).subscribe({
      next: (value) => {
        if (value <= 0) {
          this.hasUnsavedChanges = false;
        }
      },
    });

    this.formService.formChange.pipe(takeUntil(this.destroy)).subscribe({
      next: () => {
        this.bindFormSubscriptions();
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  private bindFormSubscriptions(): void {
    if (!this.formService.form.controls[SUMMARY_FORM_NAME]) {
      return;
    }

    this.form.valueChanges
      .pipe(takeUntil(this.destroy), takeUntil(this.formService.formChange))
      .subscribe({
        next: () => {
          this.hasUnsavedChanges = true;
        },
      });
  }

  onInterestToggle(event: boolean): void {
    this.isInterestAdded.patchValue(event);
  }

  onFlatRateCostClaimToggle(event: boolean): void {
    this.isFlatRateCostClaimAdded.patchValue(event);
  }

  async startWithUnknownPayment(): Promise<void> {
    if (!this.formService.case || !this.isSelectedProductAvailable) {
      return;
    }

    if (this.form.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }
    try {
      await this.casesService.startWithUnknownFees(this.formService.case.id);
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        const notDraftError = caseNotDraftError.safeParse(error.response?.data);
        if (notDraftError.success) {
          this.formService.redirectToCases(
            notDraftError.data.payee_case_reference_id,
          );
          return;
        }
      }
    }
    this.formService.pushPurchaseEventToDataLayer('unknown');
    await this.formService.redirectAfterCaseStarted();
  }

  async startInstantly(): Promise<void> {
    if (!this.formService.case || !this.isSelectedProductAvailable) {
      return;
    }

    if (this.form.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    try {
      await this.formService.startInstantly();
    } catch (error) {
      console.error('Error while starting instantly', error);
    }
  }

  async startCaseWithPayment(): Promise<void> {
    if (!this.formService.case || !this.isSelectedProductAvailable) {
      return;
    }

    if (this.form.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }
    if (this.paymentMethod.value === 'card') {
      try {
        await this.formService.startWithCard();
      } catch (error) {
        console.error('Error while starting with card', error);
      }
    } else if (this.paymentMethod.value === 'transfer') {
      try {
        const proFormaInvoiceNumber =
          await this.formService.startWithTransfer();
        this.openCaseStartSuccessDialog(
          proFormaInvoiceNumber ||
            this.formService.case?.payee_case_reference_id ||
            '',
        );
      } catch (error) {
        console.error('Error while starting with transfer', error);
      }
    }
  }

  selectProductType(type: ProductType, event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('a')) {
      return;
    }

    this.productType.setValue(type);
  }

  private openCaseStartSuccessDialog(reference: string) {
    if (!this.formService.case) {
      return;
    }

    const data: StartWithTransferDialogData = {
      currency_iso: this.summary.fees.start[0]?.currency_iso,
      reference,
      transfer_amount: this.startCaseFeeAmount!,
    };
    const dialogRef = this.dialog.open(StartWithTransferDialogComponent, {
      data,
      position: {
        top: '50px',
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(async () => {
      this.formService.redirectToCases();
    });
  }
}
